import { Box, Link, Typography, useTheme } from "@mui/material";
import { useTextOverflow } from "../hooks/useTextOverflow";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

/**
 * Renders a block of text that collapses to `lineClamp` lines and exposes
 * a Show more / Show less toggle when the content overflows. The shadow
 * element measures the natural height; we compare that to the clamped
 * element to decide whether the toggle should appear.
 *
 * @param {string} text             - The text to render
 * @param {number} lineClamp        - Lines to show when collapsed (default 2)
 * @param {string} variant          - MUI typography variant (default "body2")
 * @param {string} whiteSpace       - CSS white-space value. Pass "pre-wrap"
 *                                    to preserve user-authored line breaks.
 * @param {boolean} blendColor      - When true (default), the Show more
 *                                    gradient blends background.paper with
 *                                    the dark-mode elevation overlay so it
 *                                    matches an elevated card. Set to false
 *                                    on flat surfaces (no elevation) so the
 *                                    gradient targets the raw base colour.
 *                                    Ignored when bgColor is provided.
 * @param {string}  bgColor         - Explicit CSS colour for the gradient
 *                                    target (e.g. theme.palette.secondary.main).
 *                                    When set, overrides the blendColor logic.
 */
export function ExpandableTypography({
  text,
  lineClamp = 2,
  variant = "body2",
  whiteSpace = "normal",
  blendColor = true,
  bgColor = null,
}) {
  const theme = useTheme();
  // Font metrics drive both the visible Typography and the hidden shadow
  // used for overflow measurement — they must match exactly.
  const { fontSize, lineHeight } = theme.typography[variant];
  const typographyMetrics = { fontSize, lineHeight };

  const { clampedRef, shadowRef, canExpand, isExpanded, setIsExpanded } =
    useTextOverflow(text);

  return (
    <Box sx={{ position: "relative", width: "100%" }}>
      {/* Hidden shadow for overflow measurement — always unclamped. Clipped
          to this component's bounds, or its full-length text would overhang
          the clamped lines and add phantom scroll height to any scrolling
          ancestor (e.g. LibraryModal's tab panel). Clipping doesn't affect
          the shadow's offsetHeight, which is all useTextOverflow reads. */}
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          inset: 0,
          overflow: "hidden",
          visibility: "hidden",
          pointerEvents: "none",
        }}
      >
        <Box
          ref={shadowRef}
          sx={{
            ...typographyMetrics,
            width: "100%",
            whiteSpace,
            fontFamily: "inherit",
          }}
        >
          {text}
        </Box>
      </Box>

      <Box sx={{ position: "relative", width: "100%" }}>
        <Typography
          ref={clampedRef}
          variant={variant}
          sx={{
            ...typographyMetrics,
            whiteSpace,
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
            WebkitLineClamp: isExpanded ? "unset" : lineClamp,
            overflow: "hidden",
          }}
        >
          {text}
        </Typography>

        {/* Collapsed: gradient + Show more sits over the last line */}
        {canExpand && !isExpanded && (
          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              right: 0,
              background: (theme) => {
                let target;
                if (bgColor) {
                  target = bgColor;
                } else {
                  // MUI dark mode overlays rgba(255,255,255,0.05) on paper at
                  // elevation 1 — blend manually so the gradient matches when
                  // sitting on an elevated card. On flat surfaces (blendColor
                  // = false) target the raw base colour instead.
                  const base = theme.palette.background.paper;
                  target =
                    blendColor && theme.palette.mode === "dark"
                      ? `color-mix(in srgb, ${base}, white 5%)`
                      : base;
                }
                return `linear-gradient(to right, transparent, ${target} 40%)`;
              },
              pl: 10,
              display: "flex",
              alignItems: "flex-end",
            }}
          >
            <Link
              component="button"
              variant={variant}
              onClick={() => setIsExpanded(true)}
              underline="none"
              sx={{
                color: "text.secondary",
                fontWeight: 500,
                cursor: "pointer",
                border: "none",
                p: 0,
                lineHeight: "inherit",
                whiteSpace: "nowrap",
                display: "inline-flex",
                alignItems: "center",
                "&:hover": { color: "text.primary" },
              }}
            >
              Show more
              <ExpandMoreIcon sx={{ fontSize: "0.9rem", ml: "2px" }} />
            </Link>
          </Box>
        )}

        {/* Expanded: Show less sits below the full text */}
        {canExpand && isExpanded && (
          <Link
            component="button"
            variant={variant}
            onClick={() => setIsExpanded(false)}
            underline="none"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              mt: 0.25,
              color: "text.secondary",
              fontWeight: 500,
              cursor: "pointer",
              border: "none",
              p: 0,
              "&:hover": { color: "text.primary" },
            }}
          >
            Show less
            <ExpandLessIcon sx={{ fontSize: "0.9rem", ml: "2px" }} />
          </Link>
        )}
      </Box>
    </Box>
  );
}
